import React, { useEffect, useState } from 'react';
import { campaignsApi } from '../api/client';
import type { Campaign } from '../api/client';
import './Campaigns.css';

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    setting: '',
    tone: '',
    themes: '',
    factions: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await campaignsApi.getCampaigns();
      setCampaigns(response.data);
    } catch (err) {
      setError('Failed to load campaigns');
      console.error('Campaigns error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCampaign = async () => {
    if (!newCampaign.name.trim() || !newCampaign.setting.trim()) return;

    try {
      const campaignData = {
        name: newCampaign.name,
        setting: newCampaign.setting,
        tone: newCampaign.tone,
        themes: newCampaign.themes.split(',').map(t => t.trim()).filter(t => t),
        factions: newCampaign.factions.split(',').map(f => f.trim()).filter(f => f)
      };

      await campaignsApi.createCampaign(campaignData);
      await fetchCampaigns();
      setShowAddModal(false);
      resetNewCampaign();
    } catch (err) {
      console.error('Error adding campaign:', err);
      setError('Failed to add campaign');
    }
  };

  const handleDeleteCampaign = async (name: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await campaignsApi.deleteCampaign(name);
      await fetchCampaigns();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError('Failed to delete campaign');
    }
  };

  const resetNewCampaign = () => {
    setNewCampaign({
      name: '',
      setting: '',
      tone: '',
      themes: '',
      factions: ''
    });
  };

  const getCampaignTypeIcon = (setting: string) => {
    const lower = setting.toLowerCase();
    if (lower.includes('fantasy')) return '🏰';
    if (lower.includes('cyberpunk') || lower.includes('shadowrun')) return '🌃';
    if (lower.includes('star wars') || lower.includes('space')) return '🚀';
    if (lower.includes('modern')) return '🏙️';
    if (lower.includes('horror')) return '👻';
    if (lower.includes('western')) return '🤠';
    return '🎭';
  };

  const getThemeColor = (theme: string) => {
    const colors: { [key: string]: string } = {
      'heroism': '#e74c3c',
      'discovery': '#3498db',
      'moral choices': '#9b59b6',
      'friendship': '#f39c12',
      'power and responsibility': '#e67e22',
      'technology vs magic': '#2ecc71',
      'corporate control': '#34495e',
      'underground resistance': '#95a5a6',
      'identity': '#8e44ad',
      'good vs evil': '#c0392b',
      'redemption': '#27ae60',
      'legacy': '#d35400',
      'hope': '#f1c40f'
    };
    return colors[theme.toLowerCase()] || '#7f8c8d';
  };

  if (loading) {
    return <div className="campaigns-page"><div className="loading">Loading campaigns...</div></div>;
  }

  if (error) {
    return <div className="campaigns-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="campaigns-page">
      <header className="page-header">
        <h1>🎲 TTRPG Campaigns</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Create New Campaign
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="campaigns-stats">
        <div className="stat-card">
          <span className="stat-number">{campaigns.length}</span>
          <span className="stat-label">Total Campaigns</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{campaigns.filter(c => c.updatedAt > c.createdAt).length}</span>
          <span className="stat-label">Active Campaigns</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{campaigns.reduce((acc, c) => acc + c.themes.length, 0)}</span>
          <span className="stat-label">Total Themes</span>
        </div>
      </div>

      <div className="campaigns-grid">
        {campaigns.map((campaign) => (
          <div key={campaign.name} className="campaign-card">
            <div className="campaign-header">
              <div className="campaign-title">
                <span className="campaign-icon">{getCampaignTypeIcon(campaign.setting)}</span>
                <h3 className="campaign-name">{campaign.name}</h3>
              </div>
              <button 
                className="delete-btn"
                onClick={() => handleDeleteCampaign(campaign.name)}
                title="Delete campaign"
              >
                ×
              </button>
            </div>

            <div className="campaign-content">
              <div className="campaign-setting">
                <span className="setting-label">Setting:</span>
                <p className="setting-text">{campaign.setting}</p>
              </div>

              <div className="campaign-tone">
                <span className="tone-label">Tone:</span>
                <p className="tone-text">{campaign.tone}</p>
              </div>

              {campaign.themes && campaign.themes.length > 0 && (
                <div className="campaign-themes">
                  <span className="themes-label">Themes:</span>
                  <div className="themes-list">
                    {campaign.themes.map((theme, index) => (
                      <span 
                        key={index} 
                        className="theme-tag"
                        style={{ backgroundColor: getThemeColor(theme) }}
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {campaign.factions && campaign.factions.length > 0 && (
                <div className="campaign-factions">
                  <span className="factions-label">Factions:</span>
                  <div className="factions-list">
                    {campaign.factions.map((faction, index) => (
                      <span key={index} className="faction-tag">
                        {faction}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="campaign-dates">
                <div className="date-info">
                  <span className="date-label">Created:</span>
                  <span className="date-value">{new Date(campaign.createdAt).toLocaleDateString()}</span>
                </div>
                {campaign.updatedAt !== campaign.createdAt && (
                  <div className="date-info">
                    <span className="date-label">Updated:</span>
                    <span className="date-value">{new Date(campaign.updatedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {campaigns.length === 0 && (
          <div className="no-campaigns">
            <span className="no-campaigns-icon">🎲</span>
            <p>No campaigns created yet. Start your first adventure!</p>
          </div>
        )}
      </div>

      {/* Add Campaign Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Campaign</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Quickstone, Shadowrun Chronicles..."
                />
              </div>

              <div className="form-group">
                <label>Setting *</label>
                <textarea
                  value={newCampaign.setting}
                  onChange={(e) => setNewCampaign({...newCampaign, setting: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Describe the world and setting of your campaign..."
                />
              </div>

              <div className="form-group">
                <label>Tone</label>
                <textarea
                  value={newCampaign.tone}
                  onChange={(e) => setNewCampaign({...newCampaign, tone: e.target.value})}
                  className="form-textarea"
                  rows={2}
                  placeholder="What's the overall tone and mood of the campaign?"
                />
              </div>

              <div className="form-group">
                <label>Themes (comma-separated)</label>
                <input
                  type="text"
                  value={newCampaign.themes}
                  onChange={(e) => setNewCampaign({...newCampaign, themes: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Heroism, Discovery, Moral choices, Friendship..."
                />
              </div>

              <div className="form-group">
                <label>Factions (comma-separated)</label>
                <input
                  type="text"
                  value={newCampaign.factions}
                  onChange={(e) => setNewCampaign({...newCampaign, factions: e.target.value})}
                  className="form-input"
                  placeholder="e.g., The Iron Guard, Mage's Circle, Merchant Guild..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddCampaign}
                disabled={!newCampaign.name.trim() || !newCampaign.setting.trim()}
              >
                Create Campaign
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;